<script lang="ts">
    import { onMount } from "svelte";
    import type { Location } from "../../server";
    import LoadingScreen from "./screens/LoadingScreen.svelte";
    import LocationScreen from "./screens/LocationScreen.svelte";
    import TestAudioScreen from "./screens/TestAudioScreen.svelte";
    import PlayerScreen from "./screens/PlayerScreen.svelte";
    import { v4 as uuid } from "uuid";
    import { connectSocket, type SocketMethods } from "./socket";
    import { Audio } from "./audio";
    import ConnectionIndicator from "./components/ConnectionIndicator.svelte";
    import type { Concert } from "../../static";

    let connected = $state(false);
    let socket = $state<SocketMethods>();
    let loadingStatus = $state<string | undefined>("Connecting...");
    let concert = $state<Concert | null>(null);
    let location = $state<Location>();
    let testedAudio = $state(false);

    const audio = new Audio();

    onMount(() => {
        let clientId = sessionStorage.getItem("clientId");
        if (!clientId) {
            clientId = uuid();
            sessionStorage.setItem("clientId", clientId);
        }

        socket = connectSocket({
            clientId,
            onchangeconnected: (newConnected) => {
                if (!connected && newConnected) {
                    loadingStatus = "Waiting to start...";
                }

                if (!newConnected) {
                    audio.stopAll();
                }

                connected = newConnected;
            },
            onsetglobalvolume: (globalVolume) => audio.setGlobalVolume(globalVolume),
            onsetconcert: async (newConcert) => {
                if (newConcert != null) {
                    loadingStatus = "Loading audio...";

                    await audio.preload(newConcert, (count, total) => {
                        loadingStatus = `Loaded ${count} of ${total}`;
                    });

                    loadingStatus = undefined;
                } else if (concert != null) {
                    window.location.href = concert.redirectUrl;
                }

                concert = newConcert;
            },
            onplay: (name, volume) => {
                audio.playSound(name, volume);
            },
            onstop: (name) => {
                audio.stopSound(name);
            },
            onstopall: () => {
                audio.stopAll();
            },
        });

        const params = new URLSearchParams(window.location.search);

        if (params.has("x") && params.has("y")) {
            location = {
                x: parseFloat(params.get("x")!),
                y: parseFloat(params.get("y")!),
            };

            socket.setLocation(location);
        }

        testedAudio = params.has("testedAudio");
    });

    $effect(() => {
        const url = new URL(window.location.href);

        if (location != null) {
            url.searchParams.set("x", location.x.toString());
            url.searchParams.set("y", location.y.toString());
        } else {
            url.searchParams.delete("x");
            url.searchParams.delete("y");
        }

        if (testedAudio) {
            url.searchParams.set("testedAudio", "true");
        } else {
            url.searchParams.delete("testedAudio");
        }

        window.history.replaceState({}, "", url.toString());
    });

    const onsubmitlocation = (newLocation: Location) => {
        location = newLocation;
        socket?.setLocation(location);
    };

    const ontestaudio = () => {
        if (concert == null) return;

        const { name } = concert.audioFiles[0];
        audio.stopSound(name);
        audio.playSound(name, 1);
    };

    const ondonetestingaudio = () => {
        audio.stopAll();
        testedAudio = true;
    };
</script>

<div class="flex size-full max-h-full flex-col">
    {#if concert != null}
        <div
            class="absolute inset-0 -z-1 bg-center bg-no-repeat opacity-75"
            style:background-size={concert.visualizer.background}
            style:background-image="url({import.meta.env.VITE_STATIC_URL}{concert.backgroundImage})"
        ></div>
    {/if}

    {#if loadingStatus != null || concert == null}
        <LoadingScreen status={loadingStatus ?? "Loading concert..."} />
    {:else if location == null}
        <LocationScreen {concert} onsubmit={onsubmitlocation} />
    {:else if !testedAudio}
        <TestAudioScreen {ontestaudio} ondone={ondonetestingaudio} />
    {:else}
        <PlayerScreen {concert} {audio} />
    {/if}
</div>

<ConnectionIndicator {connected} />
